import { cn } from "@/lib/utils";
import { Bot, User, Loader2, Flame, Brain, Zap, Skull, Dumbbell } from "lucide-react";
import type { ChatMessage, BotIcon } from "@/types/evolve";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Map bot icon values to Lucide components
const BOT_ICON_COMPONENTS: Record<BotIcon, React.ComponentType<{ className?: string }>> = {
  flame: Flame,
  bot: Bot,
  brain: Brain,
  zap: Zap,
  skull: Skull,
  dumbbell: Dumbbell,
};

interface ChatBubbleProps {
  message: ChatMessage;
  isLoading?: boolean;
  botIcon?: BotIcon;
}

export function ChatBubble({ message, isLoading = false, botIcon = 'flame' }: ChatBubbleProps) {
  const isAI = message.isAI;
  const showLoadingIndicator = isAI && !message.text && isLoading;
  const BotIconComponent = BOT_ICON_COMPONENTS[botIcon] || Flame;

  return (
    <div 
      className={cn(
        "flex gap-2 animate-fade-in-up",
        isAI ? "flex-row-reverse" : "flex-row"
      )}
    >
      <div className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
        isAI ? "evolve-gradient evolve-glow" : "bg-secondary"
      )}>
        {isAI ? (
          <BotIconComponent className="w-4 h-4 text-foreground" />
        ) : (
          <User className="w-4 h-4 text-muted-foreground" />
        )}
      </div>

      <div className={cn(
        "max-w-[80%] px-4 py-3",
        isAI ? "chat-bubble-ai" : "chat-bubble-user"
      )}>
        {showLoadingIndicator ? (
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Thinking...</span>
          </div>
        ) : isAI ? (
          <div className="prose prose-sm prose-invert max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h1 className="text-base font-display font-bold text-foreground mb-2">{children}</h1>,
                h2: ({ children }) => <h2 className="text-sm font-display font-bold text-foreground mt-3 mb-2">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sm font-display font-bold text-foreground mt-2 mb-1">{children}</h3>,
                p: ({ children }) => <p className="text-sm text-foreground/90 mb-2 leading-relaxed">{children}</p>,
                ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mb-2 text-sm">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 mb-2 text-sm">{children}</ol>,
                li: ({ children }) => <li className="text-foreground/90">{children}</li>,
                strong: ({ children }) => <strong className="text-foreground font-bold">{children}</strong>,
                em: ({ children }) => <em className="text-foreground/80 italic">{children}</em>,
                table: ({ children }) => (
                  <div className="overflow-x-auto my-3 rounded border border-border/30">
                    <table className="w-full text-xs border-collapse">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="bg-primary/20">{children}</thead>,
                tbody: ({ children }) => <tbody>{children}</tbody>,
                tr: ({ children }) => <tr className="border-b border-border/20">{children}</tr>,
                th: ({ children }) => <th className="px-2 py-1.5 text-left font-medium text-foreground border-r border-border/20 last:border-r-0">{children}</th>,
                td: ({ children }) => <td className="px-2 py-1.5 text-foreground/80 border-r border-border/20 last:border-r-0">{children}</td>,
                blockquote: ({ children }) => (
                  <blockquote className="border-l-2 border-primary pl-3 my-2 text-foreground/70 italic">{children}</blockquote>
                ),
                code: ({ children }) => (
                  <code className="bg-secondary/50 px-1 py-0.5 rounded text-xs text-primary">{children}</code>
                ),
              }}
            >
              {message.text}
            </ReactMarkdown>
          </div>
        ) : (
          <div>
            {/* Show user's attached media */}
            {message.mediaUrl && message.mediaType === 'image' && (
              <img 
                src={message.mediaUrl} 
                alt="Attached" 
                className="max-w-full max-h-48 rounded-lg mb-2"
                loading="lazy"
              />
            )}
            {message.mediaUrl && message.mediaType === 'video' && (
              <video 
                src={message.mediaUrl} 
                className="max-w-full max-h-48 rounded-lg mb-2"
                controls
              />
            )}
            <p className="text-sm leading-relaxed whitespace-pre-wrap">
              {message.text}
            </p>
          </div>
        )}
        {!showLoadingIndicator && (
          <p className={cn(
            "text-[10px] mt-2 opacity-60",
            isAI ? "text-right" : "text-left"
          )}>
            {message.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>
    </div>
  );
}